import type { ContentBlock } from "../seo/types";

export interface ResourceArticle {
  slug: string;
  title: string;
  metaTitle: string;
  ogTitle?: string;
  ogDescription?: string;
  description: string;
  category: string;
  /** 40–70 word direct answer shown under the H1. */
  answer: string;
  blocks: ContentBlock[];
  related: string[];
  /** Only approved articles are listed, indexed and added to the sitemap. */
  approved: boolean;
  publishedAt: string;
}

const crisisNote =
  "In the United States and its territories, you can call or text 988 or use the 988 Lifeline chat if you are experiencing emotional distress, suicidal thoughts, or simply need crisis support. The service is free, confidential, and available 24/7. If there is an immediate life-threatening emergency, call 911. Outside the U.S., contact the appropriate crisis or emergency resource in your country.";

export const articles: ResourceArticle[] = [
  {
    slug: "where-can-i-vent-anonymously",
    title: "Where Can I Vent Anonymously? 7 Ways to Let It Out Online",
    metaTitle: "Where Can I Vent Anonymously? 7 Ways to Let It Out | Vent",
    ogTitle: "Where Can I Vent Anonymously? 7 Ways to Let It Out Online",
    description:
      "Need somewhere to vent anonymously? Explore 7 options for getting things off your chest online, including peer support, journaling, listening services and The Vent App.",
    category: "Venting & Expression",
    answer:
      "If you need somewhere to vent anonymously, you have several options: anonymous peer-support communities, private journals, listening services, support groups, and mental-wellness platforms such as [The Vent App](/anonymous-vent-app). The right choice depends on whether you want someone to listen, want feedback from people with similar experiences, prefer to write privately, or are looking for professional support.",
    approved: true,
    publishedAt: "2026-09-28",
    related: ["safe-place-to-vent-online", "what-is-anonymous-peer-support", "what-to-do-when-you-need-to-vent"],
    blocks: [
      {
        id: "quick-answer",
        note: "Quick answer: You can vent anonymously through peer-support communities, private journaling apps, anonymous listening services, topic-based support groups, and wellness platforms. The Vent App combines anonymous community conversations with topic-based rooms, journaling, self-care tools, and coping strategies.",
      },
      {
        id: "why-anonymous",
        heading: "Why do some people prefer anonymous spaces?",
        paragraphs: [
          "Some things are hard to say out loud to people who know you. You may worry about being judged, about how it will change a relationship, or about a comment following you around at work or school.",
          "An anonymous space takes some of that pressure away. You can say what you are actually feeling, without first deciding how it will look to everyone in your life.",
        ],
      },
      {
        id: "seven-ways",
        heading: "What are 7 ways to vent without sharing your identity?",
        paragraphs: ["Different options fit different moments. Here are the most common ones."],
      },
      { level: 3, heading: "1. Anonymous peer-support communities", paragraphs: ["Communities where people post under a username instead of a real name. Others can reply, relate, and offer encouragement. They work well when you want to feel heard by people who have been through something similar. This approach is often called [anonymous peer support](/resources/what-is-anonymous-peer-support)."] },
      { level: 3, heading: "2. Private journaling", paragraphs: ["Writing just for yourself, on paper or in a private app. Nobody reads it, so there is no pressure at all. Journaling is useful when you want to untangle your thoughts before sharing them with anyone."] },
      { level: 3, heading: "3. Topic-based support communities", paragraphs: ["Spaces organized around a specific experience, such as work stress, grief, or relationships. Being around people dealing with the same thing can make it easier to open up."] },
      { level: 3, heading: "4. Anonymous listening services", paragraphs: ["Some services connect you with a trained volunteer listener by text or chat. They focus on listening rather than giving advice."] },
      { level: 3, heading: "5. Mental wellness apps", paragraphs: ["Many wellness apps combine a place to write with tools such as mood tracking or coping exercises. Some also include a community."] },
      { level: 3, heading: "6. Support groups with privacy-focused participation", paragraphs: ["Some online and in-person support groups offer privacy-conscious ways to participate. Depending on the group, members may use first names, screen names, or other limited identifying information. Always review a group's privacy practices and participation rules before sharing something personal."] },
      { level: 3, heading: "7. Professional support when you need more help", paragraphs: ["A licensed mental health provider offers confidential support that peers cannot. If what you are carrying feels too heavy to manage, professional care is worth considering."] },
      {
        id: "what-makes-it-safe",
        heading: "What makes an anonymous space feel safe?",
        paragraphs: ["Choosing [a safe anonymous space](/resources/safe-place-to-vent-online) comes down to a few things to look for:"],
        list: [
          "Clear community rules and active moderation",
          "No requirement to share your real name, photo, or contacts",
          "Tools to block or report people who are unkind",
          "A culture built around support, not likes or going viral",
          "Easy access to crisis resources",
        ],
      },
      {
        id: "when-talking-helps",
        heading: "When can talking things out help, and when might you need more support?",
        paragraphs: [
          "Putting feelings into words can give you space to reflect on what you're experiencing, especially when the conversation helps you understand the situation, feel heard, or decide what you want to do next.",
          "But repeatedly going over the same upsetting event — particularly in a way that keeps you focused on anger — can sometimes reinforce rather than reduce those feelings. Research on anger regulation has found that rumination is associated with greater and more persistent anger, while approaches such as reappraisal and lowering emotional arousal can be more helpful.",
          "The goal doesn't always have to be solving the problem immediately. Sometimes you need to express what you're feeling first. Pay attention to whether the conversation leaves you with more clarity or keeps you stuck in the same emotional loop.",
          "If something is affecting your everyday life, relationships, safety, or ability to cope, consider reaching out to a qualified mental-health professional. For ideas on getting started, see [what to do when you need to vent](/resources/what-to-do-when-you-need-to-vent).",
        ],
        note: crisisNote,
      },
      {
        id: "how-vent-fits",
        heading: "How does The Vent App approach anonymous expression?",
        paragraphs: [
          "The Vent App is an anonymous venting and peer-support platform designed for people who want somewhere to express what they're going through without using a traditional public social-media identity.",
          "Members use generated usernames rather than displaying their real names to the community. You can post a Vent in topic-based rooms, reply to other people's experiences, react to conversations, or simply explore what others are sharing.",
          "Rooms cover experiences including work stress, relationships, anxiety and stressors, friendship struggles, family issues, financial stress, grief, life transitions, college life, self-reflection, and more.",
          "Vent also includes wellness tools for moments when you would rather process something privately, including:",
        ],
        list: ["A private Daily Journal", "A Self-Care Checklist", "Coping Strategies for difficult moments"],
      },
      {
        id: "how-vent-supports",
        paragraphs: [
          "For users who decide they want professional support, The Vent App also includes a directory of licensed mental-health providers.",
          "Vent provides peer support and wellness tools and is not a substitute for professional mental-health care, diagnosis, or treatment.",
        ],
      },
    ],
  },
  {
    slug: "what-is-anonymous-peer-support",
    title: "What Is Anonymous Peer Support?",
    metaTitle: "What Is Anonymous Peer Support? | The Vent App",
    ogTitle: "What Is Anonymous Peer Support?",
    description:
      "Learn how anonymous peer support works, how it differs from therapy and crisis care, and how The Vent App creates topic-based spaces for peer connection.",
    category: "Peer Support",
    answer:
      "Anonymous peer support is support between people who can relate through shared or similar lived experiences, without requiring their public identity to be the focus of the conversation. People may offer encouragement, understanding, perspective, or share what helped them personally. Unlike therapy, peer support is generally non-clinical and does not involve professional diagnosis or treatment.",
    approved: true,
    publishedAt: "2026-09-28",
    related: ["where-can-i-vent-anonymously", "safe-place-to-vent-online", "what-to-do-when-you-need-to-vent"],
    blocks: [
      {
        id: "quick-answer",
        note: "Quick answer: Anonymous peer support connects people through shared or similar lived experiences while reducing the emphasis on public identity. It can offer understanding, encouragement, and perspective, but it is not the same as therapy or crisis care. The Vent App uses generated usernames and topic-based rooms to support anonymous peer-to-peer conversations.",
      },
      {
        id: "how-it-works",
        heading: "How does anonymous peer support work?",
        paragraphs: [
          "In a peer-support space, people share what they are going through and others respond from their own experiences with understanding, encouragement, or perspective.",
          "The value comes from shared experience and mutual support, rather than someone diagnosing or treating the other person. SAMHSA describes lived experience as a core part of peer support.",
          "In an anonymous or pseudonymous community, members may use generated usernames or screen names instead of displaying their real names publicly. That can reduce some of the social pressure that comes with talking about personal experiences. If you're looking for somewhere to start, see [where can I vent anonymously?](/resources/where-can-i-vent-anonymously)",
        ],
      },
      {
        id: "peer-vs-therapy",
        heading: "How is peer support different from therapy?",
        paragraphs: [
          "Therapy is provided by a licensed mental-health professional and may involve assessment, diagnosis, treatment planning, and evidence-based therapeutic approaches.",
          "Peer support is different. It is grounded primarily in shared lived experience, mutual understanding, and encouragement rather than clinical treatment.",
          "Peer support can complement professional care, but it should not be presented as a replacement for therapy when someone needs clinical assessment or treatment.",
        ],
      },
      {
        id: "peer-vs-crisis",
        heading: "How is peer support different from crisis support?",
        paragraphs: [
          "Peer support is designed for connection, shared experiences, and encouragement. Crisis services are specifically designed to support people experiencing acute emotional distress, suicidal thoughts, or immediate safety concerns.",
          "A peer community should not be relied on as an emergency service.",
        ],
        note: "In the United States, the 988 Suicide & Crisis Lifeline provides free, confidential support by call, text, or chat, 24 hours a day. If there is an immediate medical or life-threatening emergency, call 911. Outside the U.S., use the appropriate crisis or emergency service in your country.",
      },
      {
        id: "peer-vs-friends",
        heading: "How is it different from talking to friends and family?",
        paragraphs: [
          "Friends and family know your history and can be a huge source of support. But sometimes that closeness makes things harder to say. You may not want to worry them, or the problem may involve them.",
          "Anonymous or pseudonymous peers may not have the same personal history with you as friends or family. For some people, that distance can make it easier to talk openly about something they are still trying to process. For ideas on getting started, see [what to do when you need to vent](/resources/what-to-do-when-you-need-to-vent).",
        ],
      },
      {
        id: "benefits-limits",
        heading: "What are the benefits and limitations of anonymous peer support?",
        paragraphs: ["Potential benefits can include:"],
        list: [
          "Feeling understood by people with similar experiences",
          "Having a place to put feelings into words",
          "Receiving encouragement or perspective from peers",
          "Connecting without centering the conversation on your public identity",
        ],
      },
      {
        id: "limitations",
        paragraphs: ["Important limitations include:"],
        list: [
          "Peer advice reflects personal experience, not clinical expertise",
          "Other users may misunderstand your situation",
          "Anonymous communities still require moderation and safety rules — here's how to find [a safe place to vent online](/resources/safe-place-to-vent-online)",
          "Peer support does not replace diagnosis, therapy, medication management, or emergency care",
        ],
      },
      {
        id: "how-vent-fits",
        heading: "How does The Vent App offer anonymous peer support?",
        paragraphs: [
          "The Vent App is an anonymous venting and peer-support platform built around topic-based community rooms.",
          "Members participate using generated usernames rather than displaying their real names publicly. They can post what they are going through, reply to other members, react to conversations, or simply read experiences shared by others — a place to [vent anonymously](/anonymous-vent-app).",
          "Topic-based rooms help organize conversations around experiences such as work stress, relationships, anxiety and everyday stressors, friendship struggles, family issues, grief, financial stress, life transitions, college life, and self-reflection.",
          "Vent also includes private wellness tools such as a Daily Journal, a Self-Care Checklist, and Coping Strategies for people who prefer to process something privately.",
          "For users who want professional support, The Vent App also includes a directory of licensed mental-health providers.",
          "The Vent App provides peer support and wellness tools. It does not provide therapy, diagnosis, or emergency crisis services.",
        ],
      },
    ],
  },
  {
    slug: "safe-place-to-vent-online",
    title: "How to Find a Safe Place to Vent Online",
    metaTitle: "How to Find a Safe Place to Vent Online | The Vent App",
    ogTitle: "How to Find a Safe Place to Vent Online",
    ogDescription:
      "Learn what makes an online venting community safer, including moderation, privacy, anonymous identities, reporting tools and supportive community design.",
    description:
      "Looking for a safe place to vent online? Learn what to look for in moderation, privacy, anonymity, reporting tools, peer support and crisis resources.",
    category: "Venting & Expression",
    answer:
      "Look for an online venting space with active moderation, clear rules, anonymous or pseudonymous identities, reporting and blocking tools, understandable privacy practices, and easy access to crisis resources. [The Vent App](/anonymous-vent-app) uses generated usernames, topic-based rooms, moderation, reporting tools, and privacy-focused features designed for peer support.",
    approved: true,
    publishedAt: "2026-09-28",
    related: ["where-can-i-vent-anonymously", "what-is-anonymous-peer-support", "what-to-do-when-you-need-to-vent"],
    blocks: [
      {
        id: "intro",
        paragraphs: [
          "A safer place to [vent anonymously](/anonymous-vent-app) usually has clear community rules, active moderation, privacy protections, reporting tools, and a culture built around support rather than virality. Anonymous or pseudonymous participation can reduce some social pressure, but no online platform can guarantee complete anonymity or safety.",
          "Even on anonymous platforms, people can reveal themselves through what they post, and platforms may still collect account or device data according to their privacy policies. The FTC similarly advises people to be careful about what personal and location information they share online.",
        ],
      },
      {
        id: "what-safe-means",
        heading: "What does “safe” actually mean online?",
        paragraphs: [
          "No online community can guarantee that every interaction will feel safe or supportive. A safer community reduces risk by setting clear expectations, moderating harmful behavior, giving members ways to report or block others, and limiting unnecessary exposure of personal information.",
          "Disagreement can still happen. The difference is whether the platform has systems for responding when conversations become abusive, threatening, exploitative, or otherwise violate community rules.",
        ],
      },
      {
        id: "moderation",
        heading: "Why should you look for moderation?",
        paragraphs: [
          "Moderation helps set boundaries for what is acceptable in a support-focused community. Look for published community guidelines, accessible reporting tools, blocking features, and clear information about how harmful content is handled.",
          "No moderation system catches everything, but a platform that actively enforces its rules is generally better equipped to respond to harassment, abuse, or harmful content than a completely unmoderated space.",
        ],
      },
      {
        id: "identity",
        heading: "How does identity work on the platform?",
        paragraphs: [
          "Check what other members can see about you. Does the platform display your real name, photo, location, contacts, or social accounts? Or does it allow you to participate through a generated username or screen name?",
          "Reducing the connection between your public identity and what you post can lower some of the social pressure around sharing personal experiences.",
          "It is also worth reading the platform's privacy policy. Being anonymous to other users does not necessarily mean the platform itself collects no information about your account or device.",
        ],
      },
      {
        id: "information",
        heading: "What information are you sharing?",
        paragraphs: ["Even on an anonymous platform, details in a post can identify you. Before posting, consider leaving out:"],
        list: [
          "Your full name or the names of others",
          "Your workplace or school",
          "Your exact location",
          "Phone numbers, emails, or social handles",
          "Specific addresses or regular locations",
          "Photos containing identifying details",
          "Details that could identify someone else without their permission",
        ],
        afterList:
          "Small details can add up. Someone may be able to identify you from a combination of your employer, neighborhood, job title, school, or a very specific event even if you never type your name. The FTC recommends limiting unnecessary personal and location information online.",
      },
      {
        id: "support-not-virality",
        heading: "Is the community built for support or for going viral?",
        paragraphs: [
          "Pay attention to what the platform rewards.",
          "Traditional social platforms may emphasize follower counts, shares, views, trending posts, or public engagement. A support-oriented community should place more emphasis on conversation, relevant responses, moderation, and helping people find others discussing similar experiences.",
          "The design of a platform can tell you a lot about what kind of behavior it encourages.",
        ],
      },
      {
        id: "when-not-enough",
        heading: "How do you recognize when peer support isn’t enough?",
        paragraphs: [
          "[Anonymous peer support](/resources/what-is-anonymous-peer-support) can provide connection and encouragement, but it cannot diagnose or treat a mental-health condition.",
          "Consider speaking with a licensed mental-health professional if what you are experiencing is persistent, increasingly difficult to manage, or meaningfully affecting areas such as your sleep, work, school, relationships, or daily functioning.",
          "You do not have to wait for something to become an emergency before seeking professional support.",
        ],
        note: crisisNote,
      },
      {
        id: "how-vent-fits",
        heading: "How does The Vent App approach community support?",
        paragraphs: [
          "The Vent App is an anonymous venting and peer-support platform designed around topic-based community spaces rather than traditional public social profiles.",
          "Members participate using generated usernames instead of displaying their real names to the community. They can post in topic-based rooms, reply to other members, react to conversations, or simply read what others are sharing.",
          "Vent supports community safety through published guidelines, moderation, reporting and blocking tools, and automatic redaction of certain personal details such as phone numbers and email addresses from community posts.",
          "The platform also makes crisis resources available and includes private wellness tools such as a Daily Journal, a Self-Care Checklist, and Coping Strategies.",
          "Anonymous does not mean risk-free. Members should still avoid posting information that could identify themselves or other people.",
          "The Vent App provides peer support and wellness tools and is not a substitute for professional mental-health care or emergency services.",
        ],
        links: [
          { href: "/anonymous-vent-app", label: "See how Vent works as a place to vent anonymously" },
          { href: "/resources/where-can-i-vent-anonymously", label: "Where can I vent anonymously?" },
          { href: "/resources/what-to-do-when-you-need-to-vent", label: "What to do when you need to vent" },
        ],
      },
    ],
  },
  {
    slug: "what-to-do-when-you-need-to-vent",
    title: "What to Do When You Really Need to Vent",
    metaTitle: "What to Do When You Really Need to Vent | The Vent App",
    description:
      "Need to vent? Learn practical ways to get things off your chest, talk to someone, use anonymous peer support, pause before reacting, and know when to seek more help.",
    category: "Venting & Expression",
    answer:
      "If you really need to vent, you can write privately, talk with someone you trust, use an anonymous peer-support community, or take a short pause before deciding what to do next. It can also help to decide whether you want to be heard, want advice, or need professional support.",
    approved: true,
    publishedAt: "2026-09-28",
    related: ["where-can-i-vent-anonymously", "safe-place-to-vent-online", "what-is-anonymous-peer-support"],
    blocks: [
      {
        id: "intro",
        paragraphs: [
          "When you really need to vent, start by creating some space between you and the moment. Write down what happened, name what you are feeling, or talk to someone you trust. If you would rather not involve people you know, an [anonymous peer community](/resources/what-is-anonymous-peer-support) can give you another place to express yourself. It can also help to decide whether you want someone to listen, offer perspective, or help you think through what comes next.",
        ],
      },
      {
        id: "write-it-down",
        heading: "Why write it down?",
        paragraphs: [
          "Writing can give you somewhere to organize thoughts that feel crowded or repetitive. You do not need to make it polished or even make sense of everything immediately.",
          "Start with what happened, what you are feeling, and what you wish were different. Sometimes the goal is simply to put the experience into words before deciding what to do next.",
        ],
      },
      {
        id: "name-the-feeling",
        heading: "What are you actually feeling?",
        paragraphs: [
          "Frustration is often only part of what is going on. You may also feel hurt, embarrassed, overwhelmed, disappointed, worried, rejected, or uncertain.",
          "Try putting a more specific name to what you are experiencing. You do not have to get it exactly right. The goal is to understand the feeling a little better rather than immediately reacting to it.",
        ],
      },
      {
        id: "someone-you-trust",
        heading: "Who can you talk to?",
        paragraphs: [
          "A friend, family member, or mentor who listens well can help. It can help to tell them up front what you need from the conversation.",
          "You can also tell them what you need before you start: “I mostly need you to listen,” or “I actually want your opinion on this.”",
        ],
      },
      {
        id: "anonymous-community",
        heading: "When can an anonymous peer community help?",
        paragraphs: [
          "An anonymous or pseudonymous peer community can be useful when you want to express something without connecting it to your everyday public identity.",
          "It may be especially appealing when the situation involves coworkers, family members, friends, dating, school, or another part of your life you do not want to discuss publicly.",
          "Peer communities can offer understanding, encouragement, and shared experiences, but responses come from other users rather than mental-health professionals.",
        ],
      },
      {
        id: "physical-reset",
        heading: "Can a short reset help?",
        paragraphs: [
          "Sometimes it helps to give yourself a few minutes before responding to a stressful situation.",
          "You might step away from your screen, take a short walk, slow your breathing, stretch, get a glass of water, or spend a few minutes somewhere quieter.",
          "The goal is not to ignore what you are feeling. It is simply to create enough space to choose what you want to do next.",
        ],
      },
      {
        id: "heard-or-advice",
        heading: "Do you need to be heard, or do you need advice?",
        paragraphs: [
          "These are different needs.",
          "Sometimes you want someone to listen and say, “That sounds really difficult.”",
          "Other times you want perspective, suggestions, or help deciding what to do next.",
          "Knowing which one you need can help you choose who to talk to and make it easier for the other person to respond in a way that feels useful.",
        ],
      },
      {
        id: "come-back-later",
        heading: "Why come back to the problem later?",
        paragraphs: [
          "You do not always have to solve something while you are still upset.",
          "If the situation does not require an immediate response, giving yourself some time can make it easier to separate what happened from what you want to do about it.",
          "You may decide that you want to have a conversation, set a boundary, ask for help, take action, or simply leave the situation alone.",
        ],
      },
      {
        id: "professional-support",
        heading: "When should you consider professional support?",
        paragraphs: [
          "Peer support, journaling, and talking with people you trust can be useful forms of support, but they are not substitutes for professional mental-health care.",
          "Consider reaching out to a licensed mental-health professional if what you are experiencing is persistent, increasingly difficult to manage, or meaningfully affecting your sleep, relationships, work, school, or daily life.",
          "You do not have to wait until things become an emergency to ask for professional support.",
        ],
        note: crisisNote,
      },
      {
        id: "how-vent-fits",
        heading: "How can The Vent App help when you need to vent?",
        paragraphs: [
          "The Vent App is an anonymous venting and peer-support platform designed to give people different ways to process what they are going through.",
          "If you want to talk about something with other people, you can [post anonymously](/anonymous-vent-app) in topic-based rooms covering experiences such as work stress, relationships, anxiety and stressors, friendship struggles, family issues, grief, financial stress, life transitions, and more.",
          "If you would rather keep something private, Vent also includes a Daily Journal, a Self-Care Checklist, and Coping Strategies.",
          "Members participate using generated usernames rather than displaying their real names publicly to the community.",
          "For people who want professional support, The Vent App also includes a directory of licensed mental-health providers.",
          "The Vent App provides peer support and wellness tools and is not a substitute for therapy, diagnosis, treatment, or emergency services.",
        ],
        links: [
          { href: "/anonymous-vent-app", label: "Try an app to vent anonymously" },
          { href: "/resources/where-can-i-vent-anonymously", label: "Where can I vent anonymously?" },
          { href: "/resources/safe-place-to-vent-online", label: "How to find a safe place to vent online" },
        ],
      },
    ],
  },
];

export const approvedArticles = () => articles.filter((a) => a.approved);
export const getArticle = (slug: string) => articles.find((a) => a.slug === slug);
